import { useState, useEffect } from 'react'
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom'
import {
  Users, FileText, BookOpen, MessageSquare, Activity,
  LogOut, Loader2, Shield, BarChart3, Settings, ChevronRight,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'

const ADMIN_EMAILS = (import.meta.env.VITE_ADMIN_EMAILS || '').split(',').map(e => e.trim()).filter(Boolean)
const API_BASE     = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

// ── Admin sidebar nav ─────────────────────────────────────────────────────────

const NAV = [
  { icon: BarChart3,    label: 'Overview',   path: '/admin' },
  { icon: Users,        label: 'Users',      path: '/admin/users' },
  { icon: FileText,     label: 'Documents',  path: '/admin/documents' },
  { icon: BookOpen,     label: 'Theses',     path: '/admin/theses' },
  { icon: Activity,     label: 'Activity',   path: '/admin/activity' },
  { icon: Settings,     label: 'Settings',   path: '/admin/settings' },
]

function AdminLayout({ session, onLogout, children }) {
  const location = useLocation()
  const isActive = (path) => path === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(path)

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f8fafc' }}>
      {/* Sidebar */}
      <aside className="flex flex-col w-56 shrink-0" style={{ background: '#0f172a', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
        {/* Logo */}
        <div className="flex items-center gap-2.5 h-14 px-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold"
            style={{ background: '#dc2626', color: '#fff' }}>A</div>
          <div>
            <p className="font-sans text-sm font-semibold" style={{ color: '#f1f5f9' }}>AcdyON Admin</p>
            <p className="font-mono text-xs" style={{ color: '#475569', fontSize: 9 }}>CONTROL PANEL</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(({ icon: Icon, label, path }) => {
            const active = isActive(path)
            return (
              <Link key={path} to={path}
                className="flex items-center gap-2.5 transition-all duration-150"
                style={{
                  padding: '8px 10px',
                  margin: '1px 6px',
                  borderRadius: 8,
                  color: active ? '#93c5fd' : '#64748b',
                  background: active ? 'rgba(220,38,38,0.15)' : 'transparent',
                  borderLeft: active ? '2px solid #ef4444' : '2px solid transparent',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
              >
                <Icon style={{ width: 15, height: 15, color: active ? '#f87171' : '#475569' }} />
                <span className="font-sans text-sm" style={{ fontWeight: active ? 500 : 400 }}>{label}</span>
              </Link>
            )
          })}
        </nav>

        {/* User */}
        <div className="p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
              style={{ background: '#dc2626', color: '#fff' }}>
              {(session?.user?.email || 'A')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans text-xs font-semibold truncate" style={{ color: '#e2e8f0' }}>
                {session?.user?.email?.split('@')[0] || 'Admin'}
              </p>
              <p className="font-mono text-xs" style={{ color: '#475569', fontSize: 9 }}>ADMINISTRATOR</p>
            </div>
            <button onClick={onLogout} title="Sign out"
              className="p-1.5 rounded-lg transition-colors shrink-0" style={{ color: '#475569' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.15)'; e.currentTarget.style.color = '#f87171' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569' }}>
              <LogOut style={{ width: 13, height: 13 }} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        {children}
      </div>
    </div>
  )
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="rounded-2xl p-6" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: bg }}>
          <Icon style={{ width: 18, height: 18, color }} />
        </div>
        <span className="font-sans text-sm font-medium" style={{ color: '#475569' }}>{label}</span>
      </div>
      <p className="font-mono text-3xl font-semibold" style={{ color: '#0f172a' }}>{value ?? '—'}</p>
    </div>
  )
}

// ── Overview page ─────────────────────────────────────────────────────────────

function OverviewTab({ stats, users, activity }) {
  return (
    <div className="p-8 space-y-8 max-w-5xl">
      <div>
        <h1 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '1.75rem', color: '#0f172a', fontWeight: 700 }}>
          Platform Overview
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>Live stats and recent user activity</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users}         label="Total Users"     value={stats?.users}     color="#2563eb" bg="#eff6ff" />
        <StatCard icon={FileText}      label="Documents"       value={stats?.documents} color="#7c3aed" bg="#faf5ff" />
        <StatCard icon={BookOpen}      label="Theses"          value={stats?.theses}    color="#2563eb" bg="rgba(37,99,235,0.1)" />
        <StatCard icon={MessageSquare} label="Research Chats"  value={stats?.chats}     color="#16a34a" bg="#f0fdf4" />
      </div>

      {/* Recent activity */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
        <div className="px-6 py-4" style={{ borderBottom: '1px solid #e2e8f0' }}>
          <h2 className="font-sans font-semibold text-sm" style={{ color: '#0f172a' }}>Recent Activity</h2>
        </div>
        {activity.length === 0 ? (
          <div className="text-center py-10">
            <Activity style={{ width: 28, height: 28, color: '#94a3b8', margin: '0 auto 10px', opacity: 0.5 }} />
            <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>No recent activity.</p>
          </div>
        ) : (
          <div>
            {activity.map((item, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: '#eff6ff' }}>
                  <Activity style={{ width: 13, height: 13, color: '#2563eb' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm truncate" style={{ color: '#0f172a' }}>{item.description}</p>
                  <p className="font-sans text-xs mt-0.5" style={{ color: '#94a3b8' }}>{item.user_email}</p>
                </div>
                <span className="font-mono text-xs shrink-0" style={{ color: '#94a3b8' }}>
                  {new Date(item.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Users page ────────────────────────────────────────────────────────────────

function UsersTab({ users }) {
  return (
    <div className="p-8 space-y-6 max-w-5xl">
      <div>
        <h1 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '1.75rem', color: '#0f172a', fontWeight: 700 }}>
          Users
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>{users.length} registered accounts</p>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
        {users.length === 0 ? (
          <div className="text-center py-12">
            <Users style={{ width: 32, height: 32, color: '#94a3b8', margin: '0 auto 12px', opacity: 0.5 }} />
            <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>No user data available.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  {['Email', 'Name', 'University', 'Programme', 'Joined', 'Docs', 'Theses'].map(h => (
                    <th key={h} className="text-left px-5 py-3 font-semibold" style={{ color: '#64748b', fontSize: 11, letterSpacing: '0.06em' }}>
                      {h.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} style={{ borderBottom: i < users.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <td className="px-5 py-3.5 max-w-[200px] truncate" style={{ color: '#0f172a' }}>{u.email}</td>
                    <td className="px-5 py-3.5 max-w-[140px] truncate" style={{ color: '#475569' }}>{u.full_name || '—'}</td>
                    <td className="px-5 py-3.5 max-w-[160px] truncate" style={{ color: '#475569' }}>{u.university || '—'}</td>
                    <td className="px-5 py-3.5" style={{ color: '#64748b' }}>{u.programme || '—'}</td>
                    <td className="px-5 py-3.5 font-mono text-xs" style={{ color: '#94a3b8' }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs text-center">
                      <span className="px-2 py-0.5 rounded-full" style={{ background: '#eff6ff', color: '#2563eb' }}>
                        {u.doc_count ?? 0}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs text-center">
                      <span className="px-2 py-0.5 rounded-full" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                        {u.thesis_count ?? 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Activity page ─────────────────────────────────────────────────────────────

function ActivityTab({ activity }) {
  return (
    <div className="p-8 space-y-6 max-w-5xl">
      <div>
        <h1 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '1.75rem', color: '#0f172a', fontWeight: 700 }}>
          Activity Log
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>All recent platform activity</p>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
        {activity.length === 0 ? (
          <div className="text-center py-12">
            <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>No activity to display.</p>
          </div>
        ) : (
          <div>
            {activity.map((item, i) => (
              <div key={i} className="flex items-start gap-4 px-6 py-4" style={{ borderBottom: i < activity.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: '#eff6ff' }}>
                  <Activity style={{ width: 14, height: 14, color: '#2563eb' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm" style={{ color: '#0f172a' }}>{item.description}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="font-sans text-xs" style={{ color: '#94a3b8' }}>{item.user_email}</span>
                    <span className="font-mono text-xs" style={{ color: '#cbd5e1' }}>
                      {new Date(item.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                  </div>
                </div>
                <span className="shrink-0 px-2 py-0.5 rounded-full font-mono text-xs"
                  style={{ background: item.type === 'thesis' ? 'rgba(37,99,235,0.1)' : '#eff6ff',
                           color: item.type === 'thesis' ? '#2563eb' : '#2563eb' }}>
                  {item.type || 'doc'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Settings page ─────────────────────────────────────────────────────────────

function SettingsTab() {
  return (
    <div className="p-8 max-w-2xl">
      <div className="mb-8">
        <h1 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '1.75rem', color: '#0f172a', fontWeight: 700 }}>
          Admin Settings
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>Platform configuration</p>
      </div>

      <div className="rounded-2xl p-6" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
        <div className="space-y-4">
          {[
            { label: 'Admin Email',    value: import.meta.env.VITE_ADMIN_EMAILS || 'admin@acdyon.com' },
            { label: 'API Base URL',   value: import.meta.env.VITE_API_URL || 'http://localhost:3001/api' },
            { label: 'Environment',    value: import.meta.env.MODE || 'development' },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
              <span className="font-sans text-sm font-medium" style={{ color: '#475569' }}>{label}</span>
              <span className="font-mono text-xs px-3 py-1 rounded-lg" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}>
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Root AdminPage ────────────────────────────────────────────────────────────

export default function AdminPage() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const [session,  setSession]  = useState(null)
  const [checking, setChecking] = useState(true)
  const [stats,    setStats]    = useState(null)
  const [users,    setUsers]    = useState([])
  const [activity, setActivity] = useState([])
  const [loadErr,  setLoadErr]  = useState('')

  useEffect(() => {
    const check = async () => {
      const { data: { session: s } } = await supabase.auth.getSession()
      if (!s) { navigate('/login'); return }
      const email = s.user?.email || ''
      if (ADMIN_EMAILS.length > 0 && !ADMIN_EMAILS.includes(email)) {
        setChecking(false)
        setLoadErr(`Access denied. ${email} is not an admin.`)
        return
      }
      setSession(s)
      setChecking(false)
      await loadData(s.access_token)
    }
    check()
  }, [navigate])

  const loadData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      const [statsRes, usersRes, actRes] = await Promise.allSettled([
        fetch(`${API_BASE}/admin/stats`,    { headers }).then(r => r.json()),
        fetch(`${API_BASE}/admin/users`,    { headers }).then(r => r.json()),
        fetch(`${API_BASE}/admin/activity`, { headers }).then(r => r.json()),
      ])
      if (statsRes.status === 'fulfilled') setStats(statsRes.value)
      if (usersRes.status === 'fulfilled') setUsers(Array.isArray(usersRes.value) ? usersRes.value : [])
      if (actRes.status  === 'fulfilled') setActivity(Array.isArray(actRes.value)  ? actRes.value  : [])
    } catch { setLoadErr('Failed to load admin data.') }
  }

  const handleLogout = async () => { await supabase.auth.signOut(); navigate('/login') }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8fafc' }}>
        <Loader2 style={{ width: 28, height: 28, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  if (loadErr && !session) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#f8fafc' }}>
        <Shield style={{ width: 40, height: 40, color: '#dc2626' }} />
        <p className="font-display text-lg font-semibold" style={{ color: '#0f172a' }}>Access Denied</p>
        <p className="font-sans text-sm" style={{ color: '#64748b' }}>{loadErr}</p>
        <button onClick={() => navigate('/')} className="mt-2 text-sm underline" style={{ color: '#2563eb' }}>
          Back to Home
        </button>
      </div>
    )
  }

  const path = location.pathname

  return (
    <AdminLayout session={session} onLogout={handleLogout}>
      {path === '/admin' || path === '/admin/' ? (
        <OverviewTab stats={stats} users={users} activity={activity} />
      ) : path.startsWith('/admin/users') ? (
        <UsersTab users={users} />
      ) : path.startsWith('/admin/activity') ? (
        <ActivityTab activity={activity} />
      ) : path.startsWith('/admin/settings') ? (
        <SettingsTab />
      ) : (
        <div className="p-8">
          <h1 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '1.75rem', color: '#0f172a', fontWeight: 700 }}>
            {NAV.find(n => path.startsWith(n.path) && n.path !== '/admin')?.label || 'Admin'}
          </h1>
          <p className="font-sans text-sm mt-2" style={{ color: '#94a3b8' }}>Coming soon.</p>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </AdminLayout>
  )
}
